import {
  doc,
  query,
  where,
  addDoc,
  getDoc,
  setDoc,
  getDocs,
  orderBy,
  deleteDoc,
  updateDoc,
  collection,
  getFirestore,
  serverTimestamp,
} from "firebase/firestore";
import axios from "axios";

const RETIREMENT_DATE = process.env.VUE_APP_LEGACY_RETIREMENT_DATE || "2026-11-02T00:00:00-06:00";

export function isLegacyRetired() {
  return new Date() >= new Date(RETIREMENT_DATE);
}

function toDate(value) {
  if (!value) return null;
  return typeof value.toDate === "function" ? value.toDate() : new Date(value);
}

// Llave con la que se guarda el acceso: el patrón de la ruta ("/delivery/:DocNum"),
// para que un permiso cubra cualquier documento y no solo uno.
export function legacyRouteKey(route) {
  const record = route.matched && route.matched[route.matched.length - 1];
  return (record && record.path) || route.path;
}

// Sustituye los parámetros de la ruta de origen en la URL de la nueva consola.
export function buildNewConsoleUrl(template, params = {}) {
  return template.replace(/:(\w+)/g, (match, name) =>
    name in params ? encodeURIComponent(params[name]) : match
  );
}

export async function hasLegacyAccess(email, path) {
  const snapshot = await getDoc(doc(getFirestore(), "legacy_access", email));
  if (!snapshot.exists()) return false;
  const { routes = {} } = snapshot.data();
  if (!(path in routes)) return false;
  const expiry = toDate(routes[path]);
  return !expiry || expiry > new Date();
}

export async function isLegacyAdmin(email) {
  const snapshot = await getDoc(doc(getFirestore(), "legacy_admins", email));
  return snapshot.exists();
}

export async function findLatestRequest(email, path) {
  const requests = query(
    collection(getFirestore(), "legacy_access_requests"),
    where("email", "==", email),
    where("route", "==", path)
  );
  const snapshot = await getDocs(requests);
  const sorted = snapshot.docs
    .map((item) => ({ ...item.data(), requestedAt: toDate(item.data().requestedAt) }))
    .sort((a, b) => (b.requestedAt || 0) - (a.requestedAt || 0));
  return sorted[0] || null;
}

export function requestLegacyAccess({ name, email, path, module, reason }) {
  return addDoc(collection(getFirestore(), "legacy_access_requests"), {
    name,
    email,
    route: path,
    module,
    reason,
    status: "pending",
    requestedAt: serverTimestamp(),
  });
}

export async function listRequests() {
  const requests = query(
    collection(getFirestore(), "legacy_access_requests"),
    orderBy("requestedAt", "desc")
  );
  const snapshot = await getDocs(requests);
  return snapshot.docs.map((item) => ({
    id: item.id,
    ...item.data(),
    requestedAt: toDate(item.data().requestedAt),
    expiresAt: toDate(item.data().expiresAt),
  }));
}

export async function listAccesses() {
  const snapshot = await getDocs(collection(getFirestore(), "legacy_access"));
  const rows = snapshot.docs.map((item) => {
    const { name = "", routes = {} } = item.data();
    return Object.keys(routes).map((route) => ({
      email: item.id,
      name,
      route,
      expiresAt: toDate(routes[route]),
    }));
  });
  const now = new Date();
  return [].concat(...rows)
    .filter((row) => row.expiresAt && row.expiresAt > now)
    .sort((a, b) => a.expiresAt - b.expiresAt);
}

function addHours(date, hours) {
  return new Date(date.getTime() + hours * 3600000);
}

async function grantAccess({ name, email, route, hours, reviewer }) {
  const accessRef = doc(getFirestore(), "legacy_access", email);
  const current = await getDoc(accessRef);
  const data = current.exists() ? current.data() : {};
  const routes = { ...(data.routes || {}) };
  const currentExpiry = toDate(routes[route]);
  const base = currentExpiry && currentExpiry > new Date() ? currentExpiry : new Date();
  const expiresAt = addHours(base, hours);
  routes[route] = expiresAt;
  await setDoc(
    accessRef,
    {
      name: name || data.name || "",
      routes,
      updatedBy: reviewer,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
  return expiresAt;
}

export async function approveRequest(request, { hours, note, reviewer }) {
  const expiresAt = await grantAccess({
    name: request.name,
    email: request.email,
    route: request.route,
    hours,
    reviewer,
  });
  await updateDoc(doc(getFirestore(), "legacy_access_requests", request.id), {
    status: "approved",
    note,
    expiresAt,
    reviewedBy: reviewer,
    reviewedAt: serverTimestamp(),
  });
  return expiresAt;
}

export function rejectRequest(request, { note, reviewer }) {
  return updateDoc(doc(getFirestore(), "legacy_access_requests", request.id), {
    status: "rejected",
    note,
    reviewedBy: reviewer,
    reviewedAt: serverTimestamp(),
  });
}

export function extendAccess(access, { hours, reviewer }) {
  return grantAccess({ email: access.email, route: access.route, hours, reviewer });
}

export async function revokeAccess(access) {
  const accessRef = doc(getFirestore(), "legacy_access", access.email);
  const snapshot = await getDoc(accessRef);
  if (!snapshot.exists()) return;
  const routes = { ...(snapshot.data().routes || {}) };
  delete routes[access.route];
  if (!Object.keys(routes).length) {
    await deleteDoc(accessRef);
    return;
  }
  await updateDoc(accessRef, { routes, updatedAt: serverTimestamp() });
}

export async function listAdminEmails() {
  const snapshot = await getDocs(collection(getFirestore(), "legacy_admins"));
  return snapshot.docs.map((item) => item.id);
}

function escapeHtml(text = "") {
  return String(text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export async function notifyAdmins({ name, email, module, reason }) {
  const to = process.env.NODE_ENV === "production" ? await listAdminEmails() : [email];
  if (!to.length) return;
  const adminUrl = `${window.location.origin}/accesos-temporales`;
  await axios.post("/api/sg/email/send/general", {
    to,
    replyTo: email,
    subject: `Solicitud de acceso temporal: ${module}`,
    email_body: `
      <b>${escapeHtml(name || email)}</b> pide acceso temporal a <b>${module}</b> en la consola anterior.
      <br><br>
      <b>Motivo:</b> ${escapeHtml(reason)}
      <br><br>
      Revísala en la <a href="${adminUrl}">Consola de administración</a>.
    `,
  });
}
